import React, { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Button,
  Stack,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
} from '@mui/material'
import { DataGrid, GridColDef } from '@mui/x-data-grid'
import {
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
} from '@mui/icons-material'
import { easyFarmAPI, Device } from '../lib/api'

const DevicesPage: React.FC = () => {
  const [devices, setDevices] = useState<Device[]>([])
  const [loading, setLoading] = useState(true)
  const [dialog, setDialog] = useState<{ open: boolean; device: Device | null; isEdit: boolean }>({
    open: false,
    device: null,
    isEdit: false
  })
  const [formData, setFormData] = useState<{ id: string; name: string; location: string; status: string }>({
    id: '',
    name: '',
    location: '',
    status: 'active'
  })
  const [alert, setAlert] = useState<{ show: boolean; message: string; severity: 'success' | 'error' }>({
    show: false,
    message: '',
    severity: 'success'
  })
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; deviceId: string | null; deviceName: string }>({
    open: false,
    deviceId: null,
    deviceName: ''
  })
  const [deleting, setDeleting] = useState(false)

  useEffect(() => {
    fetchDevices()
  }, [])

  const fetchDevices = async () => {
    try {
      setLoading(true)
      
      const response = await easyFarmAPI.getDevices()

      if (!response.success || !response.data) {
        console.error('Devices fetch error:', response.error)
        setDevices([])
        return
      }

      setDevices(response.data)
    } catch (error) {
      console.error('Devices fetch error:', error)
      setDevices([])
    } finally {
      setLoading(false)
    }
  }

  const columns: GridColDef[] = [
    {
      field: 'id',
      headerName: '디바이스 ID',
      width: 180,
      minWidth: 150,
      flex: 0.3,
    },
    {
      field: 'name',
      headerName: '디바이스 이름',
      width: 200,
      minWidth: 180,
      flex: 0.4,
    },
    {
      field: 'location',
      headerName: '위치',
      width: 150,
      minWidth: 120,
      flex: 0.2,
    },
    {
      field: 'status',
      headerName: '상태',
      width: 100,
      minWidth: 80,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params: any) => (
        <Chip
          label={params.value}
          color={params.value === 'active' ? 'success' : 'default'}
          size="small"
        />
      ),
    },
    {
      field: 'last_seen',
      headerName: '마지막 접속',
      width: 180,
      minWidth: 160,
      flex: 0.3,
      renderCell: (params: any) => {
        if (!params.value) return <Typography variant="body2">없음</Typography>
        try {
          const date = new Date(params.value)
          // UTC 시간을 한국 시간으로 변환
          const koreaTime = new Date(date.getTime() + (9 * 60 * 60 * 1000))
          return (
            <Typography variant="body2">
              {koreaTime.toLocaleString('ko-KR', { 
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </Typography>
          )
        } catch {
          return <Typography variant="body2">없음</Typography>
        }
      },
    },
    {
      field: 'created_at',
      headerName: '생성일',
      width: 180,
      minWidth: 160,
      flex: 0.3,
      renderCell: (params: any) => (
        <Typography variant="body2">
          {new Date(params.value).toLocaleString('ko-KR')}
        </Typography>
      ),
    },
    {
      field: 'actions',
      headerName: '작업',
      width: 120,
      minWidth: 100,
      sortable: false,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params: any) => (
        <Stack direction="row" spacing={1} justifyContent="center">
          <Tooltip title="편집">
            <IconButton size="small" onClick={() => handleEdit(params.row)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="삭제">
            <IconButton size="small" onClick={() => handleDelete(params.row.id)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ]

  const handleEdit = (device: Device) => {
    setFormData({
      id: device.id,
      name: device.name,
      location: device.location || '',
      status: device.status
    })
    setDialog({ open: true, device, isEdit: true })
  }

  const handleDelete = (deviceId: string) => {
    const device = devices.find(d => d.id === deviceId)
    setDeleteDialog({
      open: true,
      deviceId: deviceId,
      deviceName: device?.name || deviceId
    })
  }

  const confirmDelete = async () => {
    if (!deleteDialog.deviceId) return
    
    try {
      setDeleting(true)
      console.log('삭제 요청 시작:', deleteDialog.deviceId)
      
      const response = await easyFarmAPI.deleteDevice(deleteDialog.deviceId)
      
      console.log('삭제 응답:', response)
      
      if (!response.success) {
        console.error('삭제 실패:', response.error)
        setAlert({ show: true, message: `삭제 실패: ${response.error || '알 수 없는 오류'}`, severity: 'error' })
        setDeleteDialog({ open: false, deviceId: null, deviceName: '' })
        return
      }
      
      // 삭제 성공 후 목록에서 즉시 제거 (낙관적 업데이트)
      setDevices(prevDevices => prevDevices.filter(d => d.id !== deleteDialog.deviceId))
      
      setAlert({ show: true, message: '디바이스가 성공적으로 삭제되었습니다.', severity: 'success' })
      setDeleteDialog({ open: false, deviceId: null, deviceName: '' })
      
      // 목록 새로고침 (서버에서 최신 상태 확인)
      await fetchDevices()
    } catch (error) {
      console.error('Delete error:', error)
      setAlert({ show: true, message: `삭제 중 오류가 발생했습니다: ${error instanceof Error ? error.message : '알 수 없는 오류'}`, severity: 'error' })
      setDeleteDialog({ open: false, deviceId: null, deviceName: '' })
      // 오류 발생 시에도 목록 새로고침 (동기화)
      await fetchDevices()
    } finally {
      setDeleting(false)
    }
  }

  const handleAdd = () => {
    setFormData({
      id: '',
      name: '',
      location: '',
      status: 'active'
    })
    setDialog({ open: true, device: null, isEdit: false })
  }

  const handleSave = async () => {
    try {
      if (dialog.isEdit) {
        // 업데이트
        const response = await easyFarmAPI.updateDevice(formData.id, {
          name: formData.name,
          location: formData.location,
          status: formData.status
        })
        
        if (!response.success) {
          setAlert({ show: true, message: `수정 실패: ${response.error || '알 수 없는 오류'}`, severity: 'error' })
          return
        }
        
        setAlert({ show: true, message: '디바이스가 성공적으로 수정되었습니다.', severity: 'success' })
      } else {
        // 생성
        if (!formData.id || !formData.name) {
          setAlert({ show: true, message: '디바이스 ID와 이름은 필수입니다.', severity: 'error' })
          return
        }
        
        const response = await easyFarmAPI.createDevice({
          id: formData.id,
          name: formData.name,
          location: formData.location || undefined,
          status: formData.status
        })
        
        if (!response.success) {
          setAlert({ show: true, message: `생성 실패: ${response.error || '알 수 없는 오류'}`, severity: 'error' })
          return
        }
        
        setAlert({ show: true, message: '디바이스가 성공적으로 생성되었습니다.', severity: 'success' })
      }
      
      setDialog({ open: false, device: null, isEdit: false })
      await fetchDevices()
    } catch (error) {
      console.error('Save error:', error)
      setAlert({ show: true, message: `저장 중 오류가 발생했습니다: ${error instanceof Error ? error.message : '알 수 없는 오류'}`, severity: 'error' })
    }
  }

  useEffect(() => {
    if (alert.show) {
      const timer = setTimeout(() => {
        setAlert(prev => ({ ...prev, show: false }))
      }, 3000)
      return () => clearTimeout(timer)
    }
  }, [alert.show])

  return (
    <Box>
      {alert.show && (
        <Alert 
          severity={alert.severity} 
          sx={{ mb: 3 }}
          onClose={() => setAlert(prev => ({ ...prev, show: false }))}
        >
          {alert.message}
        </Alert>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" sx={{ color: '#1877F2', fontWeight: 700 }}>
          디바이스 관리
        </Typography>
        <Stack direction="row" spacing={2}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAdd}
          >
            디바이스 추가
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchDevices}
          >
            새로고침
          </Button>
        </Stack>
      </Box>

      <Card>
        <CardContent>
          <Box sx={{ height: 600, width: '100%' }}>
            <DataGrid
              rows={devices}
              columns={columns}
              loading={loading}
              pageSizeOptions={[25, 50, 100]}
              initialState={{
                pagination: {
                  paginationModel: { pageSize: 25 },
                },
              }}
              disableRowSelectionOnClick
              autoHeight
              sx={{
                '& .MuiDataGrid-cell': {
                  borderBottom: '1px solid rgba(145, 158, 171, 0.12)',
                  fontSize: '0.875rem',
                },
                '& .MuiDataGrid-columnHeaders': {
                  backgroundColor: 'rgba(24, 119, 242, 0.08)',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  color: '#1877F2',
                },
                '& .MuiDataGrid-row:hover': {
                  backgroundColor: 'rgba(24, 119, 242, 0.04)',
                },
                '& .MuiDataGrid-cell:focus': {
                  outline: 'none',
                },
                '& .MuiDataGrid-footerContainer': {
                  borderTop: '1px solid rgba(145, 158, 171, 0.12)',
                },
                '& .MuiDataGrid-root': {
                  border: 'none',
                },
              }}
            />
          </Box>
        </CardContent>
      </Card>

      {/* 삭제 확인 다이얼로그 */}
      <Dialog 
        open={deleteDialog.open} 
        onClose={() => !deleting && setDeleteDialog({ open: false, deviceId: null, deviceName: '' })} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>디바이스 삭제 확인</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            정말로 <strong>"{deleteDialog.deviceName}"</strong> 디바이스를 삭제하시겠습니까?
          </Typography>
          <Alert severity="warning" sx={{ mt: 2 }}>
            이 작업은 되돌릴 수 없습니다. 다음 데이터가 함께 삭제됩니다:
            <ul style={{ marginTop: 8, marginBottom: 0, paddingLeft: 20 }}>
              <li>모든 센서 데이터</li>
              <li>모든 제어 로그</li>
              <li>모든 임계치 설정</li>
            </ul>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setDeleteDialog({ open: false, deviceId: null, deviceName: '' })} 
            disabled={deleting}
          >
            취소
          </Button>
          <Button 
            onClick={confirmDelete} 
            variant="contained" 
            color="error"
            disabled={deleting}
          >
            {deleting ? '삭제 중...' : '삭제'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 편집/추가 다이얼로그 */}
      <Dialog open={dialog.open} onClose={() => setDialog({ open: false, device: null, isEdit: false })} maxWidth="sm" fullWidth>
        <DialogTitle>
          {dialog.isEdit ? '디바이스 편집' : '새 디바이스 추가'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="디바이스 ID"
              value={formData.id}
              onChange={(e) => setFormData(prev => ({ ...prev, id: e.target.value }))}
              fullWidth
              required
              disabled={dialog.isEdit}
              helperText={dialog.isEdit ? '디바이스 ID는 변경할 수 없습니다.' : '고유한 디바이스 ID를 입력하세요.'}
            />
            <TextField
              label="디바이스 이름"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              fullWidth
              required
            />
            <TextField
              label="위치"
              value={formData.location}
              onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel>상태</InputLabel>
              <Select
                value={formData.status}
                label="상태"
                onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
              >
                <MenuItem value="active">활성</MenuItem>
                <MenuItem value="inactive">비활성</MenuItem>
                <MenuItem value="maintenance">점검 중</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog({ open: false, device: null, isEdit: false })}>
            취소
          </Button>
          <Button onClick={handleSave} variant="contained">
            {dialog.isEdit ? '수정' : '추가'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default DevicesPage
